import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useModulePermission } from '../utils/useModulePermission';

import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Paper,
  useMediaQuery,
} from '@mui/material';
import {
  AttachMoney,
  CheckCircle,
  Warning,
  Schedule,
  EmojiEvents,
  Calculate,
  PriceChange,
  RequestQuote
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import CompactMetricCard from '../components/common/CompactMetricCard';
import apiClient from '../api/client';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Types for the API responses
interface OverviewData {
  monthlyBreakdown: Array<{
    month: string;
    category: string;
    percentage: number;
  }>;
  summaryMetrics: {
    totalEmployees: number;
    totalProjects: number;
    totalAllocations: number;
    billedPercentage: number;
    unbilledPercentage: number;
    benchPercentage: number;
    unallocatedPercentage: number;
  };
}

interface FinancialTrendsData {
  financialTrends: Array<{
    month: string;
    revenue: number;
    gmPercentage: number;
  }>;
}

interface DashboardPageProps {
  startDate: string | null;
  endDate: string | null;
  /** From App: `fetchActualsThrough()` / actualsThroughUpdated event */
  actualsThroughDate?: string | null;
}


interface FinanceData {
  period: {
    start_date: string;
    end_date: string;
  };
  metrics: FinanceMetrics;
  breakdown: {
    total_revenue: number;
    last_month_revenue: number;
    gross_margin_percentage: number;
    last_month_gross_margin_percentage: number;
    ebitda_percentage: number;
    last_month_ebitda_percentage: number;
  };
  monthly_data: MonthlyData[];
}


interface FinanceMetrics {
  revenue: {
    period_value: number;
    last_month_value: number;
    currency: string;
  };
  gross_margin: {
    period_percentage: number;
    last_month_percentage: number;
  };
  ebitda: {
    period_percentage: number;
    last_month_percentage: number;
  };
  receivable_invoice_amount: number;
}

interface MonthlyData {
  month: string;
  month_key: string;
  revenue: number;
  gross_profit: number;
  ebitda: number;
  cost_of_sales: number;
  sga_expenses: number;
  corporate_staff_cost: number;
  travel_expenses: number;
}

const ONE_LAKH = 100_000;

/** Receivables: lakhs + L when over ₹1L; otherwise Indian-style comma grouping. */
function formatReceivableInvoiceDisplay(amount: number | undefined | null): string {
  if (amount == null || Number.isNaN(Number(amount))) return '₹0';
  const n = Number(amount);
  if (n === 0) return '₹0';
  if (n > ONE_LAKH) return `₹${(n / ONE_LAKH).toFixed(1)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

/** PnL metric: month keys (e.g. apr_25) plus ytd, ytd_pct — allows string index for chart lookups. */
interface PnlMetric {
  [key: string]: number;
}

/** Sample PnL data with negative/mixed values for testing charts (e.g. revenue, GM%, EBITDA, PBT). */
const samplepnlData: {
  revenue: PnlMetric;
  cost_of_sales: PnlMetric;
  gross_profit: PnlMetric;
  ebitda: PnlMetric;
  pat: PnlMetric;
  pbt: PnlMetric;
} = {
  revenue: {
    apr_25: 450000,
    may_25: -120000,
    jun_25: 420000,
    jul_25: -85000,
    aug_25: 500000,
    sep_25: 480000,
    oct_25: -45000,
    nov_25: 538000,
    dec_25: 550000,
    jan_26: 562000,
    feb_26: 575000,
    mar_26: 588000,
    ytd: 4983000,
    ytd_pct: 100,
  },
  cost_of_sales: {
    apr_25: 192000,
    may_25: -85000,
    jun_25: 200500,
    jul_25: -120000,
    aug_25: 209000,
    sep_25: 213000,
    oct_25: -60000,
    nov_25: 221500,
    dec_25: 226000,
    jan_26: 230000,
    feb_26: 234500,
    mar_26: 238500,
    ytd: 2830500,
    ytd_pct: 56.82,
  },
  gross_profit: {
    apr_25: 258000,
    may_25: -40000,
    jun_25: 219500,
    jul_25: -130000,
    aug_25: 291000,
    sep_25: 267000,
    oct_25: -39000,
    nov_25: 316500,
    dec_25: 324000,
    jan_26: 332000,
    feb_26: 340500,
    mar_26: 349500,
    ytd: 2387000,
    ytd_pct: 42.62,
  },
  ebitda: {
    apr_25: 138000,
    may_25: -85000,
    jun_25: 151200,
    jul_25: -180000,
    aug_25: 164400,
    sep_25: 171500,
    oct_25: -120000,
    nov_25: 185600,
    dec_25: 190800,
    jan_26: 197900,
    feb_26: 204000,
    mar_26: 212000,
    ytd: 1400400,
    ytd_pct: 24.98,
  },
  pat: {
    apr_25: 128050,
    may_25: -92000,
    jun_25: 140530,
    jul_25: -195000,
    aug_25: 155360,
    sep_25: 162000,
    oct_25: -135000,
    nov_25: 175780,
    dec_25: 182270,
    jan_26: 189610,
    feb_26: 195330,
    mar_26: 203620,
    ytd: 1331550,
    ytd_pct: 23.73,
  },
  pbt: {
    apr_25: 128050,
    may_25: -92000,
    jun_25: 140530,
    jul_25: -195000,
    aug_25: 155360,
    sep_25: 162000,
    oct_25: -135000,
    nov_25: 175780,
    dec_25: 182270,
    jan_26: 189610,
    feb_26: 195330,
    mar_26: 203620,
    ytd: 1331550,
    ytd_pct: 23.73,
  },
};



const ExecutiveSummaryPage = ({ startDate, endDate, actualsThroughDate }: DashboardPageProps) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    // Keep charts readable on mobile, slightly smaller on md/lg/xl
    const chartFont = useMemo(
      () => ({
        legend: isMdUp ? 10 : 11,
        ticks: isMdUp ? 9 : 10,
        tooltipTitle: isMdUp ? 12 : 13,
        tooltipBody: isMdUp ? 11 : 12,
        axisTitle: isMdUp ? 10 : 12,
        legendPadding: isMdUp ? 14 : 18,
      }),
      [isMdUp],
    );

  const { can } = useModulePermission("Dashboard");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OverviewData | null>(null);
  const [, setFinancialData] = useState<FinancialTrendsData | null>(null);
  const [pnlData, setPnlData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [targetGM, setTargetGM] = useState<number>(52);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Chart point selection (exactly two points) + last comparison (Y2 - Y1)
  const chartRef = useRef<any>(null);
  const [selectedPoints, setSelectedPoints] = useState<
    { datasetIndex: number; index: number; y: number; label: string; datasetLabel: string }[]
  >([]);
  const [lastDifference, setLastDifference] = useState<number | null>(null);
  const [lastDifferenceSuffix, setLastDifferenceSuffix] = useState<string>('');
  const [lastComparedPair, setLastComparedPair] = useState<{
    p1: { datasetIndex: number; index: number; y: number; label: string; datasetLabel: string };
    p2: { datasetIndex: number; index: number; y: number; label: string; datasetLabel: string };
  } | null>(null);
  const [financeData, setFinanceData] = useState<FinanceData | null>(null);

  const normalizeDatasetLabel = (label: string) =>
    String(label || '')
      .replace(/\(₹L\)/g, '')
      .trim()
      .toLowerCase();

  // After two points are selected, compute difference, store it, then reset selection.
  useEffect(() => {
    if (selectedPoints.length !== 2) return;
    const [p1, p2] = selectedPoints;
    const isCostOfSalesPair =
      p1.datasetLabel.toLowerCase().includes('cost') &&
      p2.datasetLabel.toLowerCase().includes('cost') &&
      p1.datasetLabel.toLowerCase() === p2.datasetLabel.toLowerCase();

    if (isCostOfSalesPair) {
      const diffPct = p1.y !== 0 ? ((p2.y - p1.y) / p1.y) * 100 : 0;
      setLastDifference(diffPct);
      setLastDifferenceSuffix(' %');
    } else {
      const diff = p2.y - p1.y;
      setLastDifference(diff);
      setLastDifferenceSuffix('');
    }
    setLastComparedPair({ p1, p2 });
    setSelectedPoints([]);
  }, [selectedPoints]);


  const fetchFinanceData = useCallback(async () => {
      if (!startDate || !endDate) return;
  
      setLoading(true);
      setError(null);
  
      try {
        const response = await apiClient.get('/api/v1/delivery/finance-dashboard', {
          params: {
            start_date: startDate,
            end_date: endDate
          }
        });
  
        setFinanceData(response.data);
        // console.log('[ExecutiveSummary] Finance dashboard data', response.data?.metrics?.receivable_invoice_amount);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to fetch finance data');
      } finally {
        setLoading(false);
      }
    }, [startDate, endDate]);

  // Helper function to get the last month from date range
  const getLastMonthValue = (data: any, field: string) => {
    if (!data || !startDate || !endDate) return 0;
    
    // Map date range to P&L months
    const monthMapping: { [key: string]: string } = {
      "2025-04": "apr_25", "2025-05": "may_25", "2025-06": "jun_25", "2025-07": "jul_25",
      "2025-08": "aug_25", "2025-09": "sep_25", "2025-10": "oct_25", "2025-11": "nov_25",
      "2025-12": "dec_25", "2026-01": "jan_26", "2026-02": "feb_26", "2026-03": "mar_26"
    };
    
    const lastMonth = monthMapping[endDate];
    return data[field]?.[lastMonth] || 0;
  };

  // Helper function to get last month's allocation percentage
  const getLastMonthAllocation = (category: string) => {
    if (!data?.monthlyBreakdown || !endDate) return 0;
    
    // Convert endDate to month format (e.g., "2025-07" -> "Jul-25")
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const [year, month] = endDate.split('-');
    const monthName = monthNames[parseInt(month) - 1];
    const monthKey = `${monthName}-${year.slice(-2)}`;
    
    const lastMonthData = data.monthlyBreakdown.find(
      (item: any) => item.month === monthKey && item.category === category
    );
    
    return lastMonthData?.percentage || 0;
  };

  // Helper function to get the month name for display
  const getLastMonthName = () => {
    if (!endDate) return "";
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const [, month] = endDate.split('-');
    return monthNames[parseInt(month) - 1];
  };

  // Helper function to generate month labels for charts based on date range
  const getChartMonthLabels = () => {
    if (!startDate || !endDate) return ['Apr-25', 'May-25', 'Jun-25', 'Jul-25'];
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const labels = [];
    
    const startYear = parseInt(startDate.split('-')[0]);
    const startMonth = parseInt(startDate.split('-')[1]);
    const endYear = parseInt(endDate.split('-')[0]);
    const endMonth = parseInt(endDate.split('-')[1]);
    
    let currentYear = startYear;
    let currentMonth = startMonth;
    
    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
      const monthName = monthNames[currentMonth - 1];
      const yearShort = currentYear.toString().slice(-2);
      labels.push(`${monthName}-${yearShort}`);
      
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    
    return labels;
  };

  const fetchData = useCallback(async (isRefresh: boolean = false) => {
    // If we already have data, show refreshing state instead of loading
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Build query parameters for date filtering
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const queryString = params.toString();
      const apiSuffix = queryString ? `?${queryString}` : '';
      
      // Fetch overview data from the real endpoint
      const overviewResponse = await apiClient.get(`/api/v1/delivery/overview-real${apiSuffix}`);
      setData(overviewResponse.data);
        
      // Fetch financial trends data
      const financialResponse = await apiClient.get(`/api/v1/delivery/financial-trends-simple${apiSuffix}`);
      setFinancialData(financialResponse.data);
      
      // Fetch P&L data with date filtering
      const pnlResponse = await apiClient.get(`/api/v1/delivery/pnl/summary${apiSuffix}`);
      const pnl = pnlResponse.data;
      setPnlData(pnl);
      console.debug('[ExecutiveSummary] PnL summary', { hasPbt: !!pnl?.pbt, pbtYtd: pnl?.pbt?.ytd, hasCostOfSales: !!pnl?.cost_of_sales, hasGrossProfit: !!pnl?.gross_profit });
    } catch (err: unknown) {
      // Error fetching data - set error state for user feedback
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
     fetchFinanceData();
  }, [fetchData]);



  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading overview data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', position: 'relative' }}>
      {/* Refresh indicator */}
      {isRefreshing && (
        <Box sx={{ 
          position: 'absolute', 
          top: 10, 
          right: 10, 
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '4px 8px',
          borderRadius: 1,
          boxShadow: 1
        }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Updating data...
      </Typography>
        </Box>
      )}
      
      {/* Date Range Display */}
      {/* {startDate && endDate && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing data for: {startDate} - {endDate}
          </Typography>
        </Box>
      )} */}

      {/* P&L Statement Section */}
      {/* <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
          Data source: Database (imported from MIS Template July 25_P&L.csv)
        </Typography>
      </Box> */}

      {/* Row 1: Revenue, Cost of Sales, GM% */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {/* revenue card */}
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="REVENUE"
            value={pnlData?.revenue?.ytd ? `₹${(pnlData.revenue.ytd / 100000).toFixed(1)}L` : "₹0L"}
            value2={pnlData?.revenue?.ytd_pct != null ? ` ${pnlData.revenue.ytd_pct}% of revenue` : undefined}
            icon={AttachMoney}
            trend={{ 
              value: getLastMonthValue(pnlData, 'revenue') ? 
                `${getLastMonthName()}: ₹${(getLastMonthValue(pnlData, 'revenue') / 100000).toFixed(1)}L` : `${getLastMonthName()}: ₹0L`, 
              isPositive: true ,
            }}
            color="primary"
          />
        </Grid>
        {/* cost of sales card */}
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="Delivery Costs"
            value={pnlData?.cost_of_sales?.ytd ? `₹${(pnlData.cost_of_sales.ytd / 100000).toFixed(1)}L` : "₹0L"}
            value2={pnlData?.cost_of_sales?.ytd_pct != null ? ` ${pnlData.cost_of_sales.ytd_pct}% of Delivery costs` : undefined}
            icon={Calculate}
            trend={{ 
              value: getLastMonthValue(pnlData, 'cost_of_sales') ? 
                `${getLastMonthName()}: ₹${(getLastMonthValue(pnlData, 'cost_of_sales') / 100000).toFixed(1)}L` : `${getLastMonthName()}: ₹0L`, 
              isPositive: false 
            }}
            color="custom1"
          />
        </Grid>
        {/* gross margin  card*/}
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="Gross margin"
            value={pnlData?.revenue?.ytd && pnlData?.cost_of_sales?.ytd ? 
              `${(((pnlData.revenue.ytd - pnlData.cost_of_sales.ytd) / pnlData.revenue.ytd) * 100).toFixed(1)}%` : "0%"}
            // value2={pnlData?.gross_profit?.ytd_pct != null ? `${pnlData.gross_profit.ytd_pct}% of Gross Margin` : undefined}
            icon={EmojiEvents}
            trend={{ 
              value: (() => {
                const lastRevenue = getLastMonthValue(pnlData, 'revenue');
                const lastCost = getLastMonthValue(pnlData, 'cost_of_sales');
                const gmPercent = lastRevenue && lastCost ? 
                  `${(((lastRevenue - lastCost) / lastRevenue) * 100).toFixed(1)}%` : "0%";
                return `${getLastMonthName()}: ${gmPercent}`;
              })(), 
              isPositive: true 
            }}
            color="success"
          />
        </Grid>
          {/* ebitda summary card */}
         <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="EBITDA"
            value={pnlData?.ebitda?.ytd ? `₹${(pnlData.ebitda.ytd / 100000).toFixed(1)}L` : "₹0L"}
            value2={pnlData?.ebitda?.ytd_pct != null ? `${pnlData.ebitda.ytd_pct}% of EBITDA` : undefined}
            icon={Calculate}
            trend={{ 
              value: getLastMonthValue(pnlData, 'ebitda') ? 
                `${getLastMonthName()}: ₹${(getLastMonthValue(pnlData, 'ebitda') / 100000).toFixed(1)}L` : `${getLastMonthName()}: ₹0L`, 
              isPositive: false 
            }}
            color="warning"
          />
        </Grid>

        {/* pbt */}
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="PBT"
            value={pnlData?.pbt?.ytd ? `₹${(pnlData.pbt.ytd / 100000).toFixed(1)}L` : "₹0L"}
            value2={pnlData?.pbt?.ytd_pct != null ? `${pnlData.pbt.ytd_pct}% of revenue` : "0%"}
            icon={PriceChange}
            trend={{ 
              value: getLastMonthValue(pnlData, 'pbt') ? 
                `${getLastMonthName()}: ₹${(getLastMonthValue(pnlData, 'pbt') / 100000).toFixed(1)}L` : `${getLastMonthName()}: ₹0L`, 
              isPositive: true 
            }}
            color="custom"
          />
        </Grid>

      {/* receivables */}
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="Receivables"
            value={formatReceivableInvoiceDisplay(financeData?.metrics?.receivable_invoice_amount)}
            value2={
              actualsThroughDate
                ? `Filter not applicable for receivables · Actuals through ${dayjs(actualsThroughDate).format('MMM YYYY')}`
                : 'Filter not applicable for receivables'
            }
            icon={RequestQuote}
            color="custom_summary_card"
          />
        </Grid>


      </Grid>

      {/* Allocation Master Section */}
      {/* <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
          Data source: Database (allocation records)
        </Typography>
      </Box> */}

      {/* Row 2: Billed, Unbilled, Bench */}
      <Grid container spacing={3} sx={{ mb: 4 }}>

        {/* billed */}
        {/* <Grid item xs={12} sm={6} md={3}>
          <CompactMetricCard
            title="BILLED %"
            value={data?.summaryMetrics?.billedPercentage ? `${data.summaryMetrics.billedPercentage}%` : "0%"}
            icon={CheckCircle}
            trend={{ 
              value: `${getLastMonthName()}: ${getLastMonthAllocation('billed').toFixed(1)}%`, 
              isPositive: true 
            }}
            color="info"
          />
        </Grid> */}

        {/* unbilled */}
        {/* <Grid item xs={12} sm={6} md={3}>
          <CompactMetricCard
            title="UNBILLED %"
            value={data?.summaryMetrics?.unbilledPercentage ? `${data.summaryMetrics.unbilledPercentage}%` : "0%"}
            icon={Warning}
            trend={{ 
              value: `${getLastMonthName()}: ${getLastMonthAllocation('unbilled').toFixed(1)}%`, 
              isPositive: false 
            }}
            color="custom2"
          />
        </Grid> */}

        {/* bench */}
        {/* <Grid item xs={12} sm={6} md={3}>
          <CompactMetricCard
            title="BENCH %"
            value={data?.summaryMetrics?.benchPercentage ? `${data.summaryMetrics.benchPercentage}%` : "0%"}
            icon={Schedule}
            trend={{ 
              value: `${getLastMonthName()}: ${getLastMonthAllocation('bench').toFixed(1)}%`, 
              isPositive: false 
            }}
            color="error"
          />
        </Grid> */}

        {/* PBT summary card — uses pbt only (no PAT) */}
        {/* <Grid item xs={12} sm={6} md={3}>
          <CompactMetricCard
            title="PBT"
            value={pnlData?.pbt?.ytd != null ? `₹${((pnlData.pbt.ytd ?? 0) / 100000).toFixed(1)}L` : "₹0L"}
            value2={pnlData?.pbt?.ytd_pct != null ? `${pnlData.pbt.ytd_pct}% of revenue` : "0%"}
            icon={PriceChange}
            trend={{
              value: getLastMonthValue(pnlData, 'pbt') ? `${getLastMonthName()}: ₹${(getLastMonthValue(pnlData, 'pbt') / 100000).toFixed(1)}L` : `${getLastMonthName()}: ₹0L`,
              isPositive: true
            }}
            color="custom"
          />
        </Grid> */}

      </Grid>
      
      {/* Target GM% Input */}
      <Box sx={{ mb: 4 }}>
            <TextField
          label="Target GM%"
              type="number"
          value={targetGM}
          onChange={(e) => setTargetGM(Number(e.target.value))}
          variant="outlined"
              size="small"
          sx={{ width: 200 }}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
        />
      </Box>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue & Cost of Sales Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 500 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Revenue, Gross Margin, EBITDA, PBT (Amount)
            </Typography>
            <Box sx={{ height: 400 }}>
              {pnlData && (
                <Line
                  ref={chartRef}
                  data={{
                    labels: getChartMonthLabels(),
                    datasets: [
                      {
                        label: 'Revenue (₹L)',
                        data: getChartMonthLabels().map(month => {
                          // Convert month label to data key (e.g., "Apr-25" -> "apr_25")
                          const [monthName, year] = month.split('-');
                          const monthKey = `${monthName.toLowerCase()}_${year}`;
                          return pnlData.revenue?.[monthKey] ? (pnlData.revenue[monthKey] / 100000) : 0;
                        }),
                        borderColor: '#1E40AF',
                        backgroundColor: 'rgba(75, 192, 192, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#1E40AF',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                      {
                        label:'Gross margin',
                        data: getChartMonthLabels().map(month => {
                          // Convert month label to data key (e.g., "Apr-25" -> "apr_25")
                          const [monthName, year] = month.split('-');
                          const monthKey = `${monthName.toLowerCase()}_${year}`;
                          return pnlData.gross_profit?.[monthKey] ? (pnlData.gross_profit[monthKey] / 100000) : 0;
                        }),
                        borderColor: '#10B981',
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#10B981',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                      {
                        label:'EBITDA',
                        data: getChartMonthLabels().map(month => {
                          // Convert month label to data key (e.g., "Apr-25" -> "apr_25")
                          const [monthName, year] = month.split('-');
                          const monthKey = `${monthName.toLowerCase()}_${year}`;
                          return pnlData.ebitda?.[monthKey] ? (pnlData.ebitda[monthKey] / 100000) : 0;
                        }),
                        borderColor: '#F59E0B',
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#F59E0B',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                      {
                        label: 'PBT',
                        data: getChartMonthLabels().map(month => {
                          // Convert month label to data key (e.g., "Apr-25" -> "apr_25")
                          const [monthName, year] = month.split('-');
                          const monthKey = `${monthName.toLowerCase()}_${year}`;
                          return pnlData.pbt?.[monthKey] ? (pnlData.pbt[monthKey] / 100000) : 0;
                        }),
                        borderColor: '#7a88b6ff',
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#7a88b6ff',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                    ],
                  }}
                  options={{
                    onClick: (_event, elements) => {
                      if (!elements || elements.length === 0) return;
                      const chart = chartRef.current;
                      const el = elements[0];
                      const datasetIndex = el.datasetIndex;
                      const index = el.index;

                      // Allow selecting points from any dataset in this chart (Revenue / Cost of Sales)
                      const rawDatasetLabel = String(chart?.data?.datasets?.[datasetIndex]?.label ?? '');
                      const datasetLabel = rawDatasetLabel.replace(/\(₹L\)/g, '').trim();

                      const label = String(chart?.data?.labels?.[index] ?? `Point ${index + 1}`);
                      const yRaw = chart?.data?.datasets?.[datasetIndex]?.data?.[index];
                      const y = typeof yRaw === 'number' ? yRaw : Number(yRaw);
                      if (!Number.isFinite(y)) return;

                      setSelectedPoints((prev) => {
                        // allow selecting exactly two points (ignore further clicks until reset)
                        if (prev.length >= 2) return prev;
                        // prevent duplicates
                        if (prev.some((p) => p.datasetIndex === datasetIndex && p.index === index)) return prev;
                        return [...prev, { datasetIndex, index, y, label, datasetLabel: datasetLabel || `Series ${datasetIndex + 1}` }];
                      });
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                      duration: 0,
                    },
                    hover: {
                      mode: 'nearest' as const,
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        labels: {
                          usePointStyle: true,
                          padding: 20,
                        },
                      },
                      tooltip: {
                        enabled: true,
                        mode: 'index' as const,
                        intersect: false,
                        animation: false,
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed.y;
                            return `${context.dataset.label}: ₹${value.toFixed(1)}L`;
                          },
                          footer: (tooltipItems) => {
                            if (!tooltipItems || tooltipItems.length === 0) return '';

                            // Realtime comparison while user has selected the first point:
                            // Compare the hovered point (same series) against the first selected point.
                            if (selectedPoints.length === 1) {
                              const p1 = selectedPoints[0];
                              const p1Series = normalizeDatasetLabel(p1.datasetLabel);

                              const hoveredSameSeries = tooltipItems.find((it) => {
                                const s = normalizeDatasetLabel(String(it.dataset?.label ?? ''));
                                return s === p1Series;
                              });
                              if (!hoveredSameSeries) return '';

                              const y2 = Number(hoveredSameSeries.parsed?.y);
                              if (!Number.isFinite(y2)) return '';

                              const y1 = p1.y;
                              const isCost = p1Series.includes('cost');
                              const diff = isCost ? (y1 !== 0 ? ((y2 - y1) / y1) * 100 : 0) : (y2 - y1);
                              const suffix = isCost ? ' %' : '';
                              const y2Label = String(hoveredSameSeries.label ?? '');

                              return [
                                `Difference (Y2 - Y1): ${diff.toFixed(2)}${suffix}`,
                                `Y1: ${p1.datasetLabel} • ${p1.label} = ${y1.toFixed(2)}`,
                                `Y2: ${p1.datasetLabel} • ${y2Label} = ${y2.toFixed(2)}`,
                              ];
                            }

                            // Otherwise, show the last comparison (but only when hovering one of those compared months for that series)
                            if (!lastComparedPair || lastDifference === null) return '';

                            const hoveredLabel = String(tooltipItems[0].label ?? '');
                            const isComparedMonth =
                              hoveredLabel === lastComparedPair.p1.label || hoveredLabel === lastComparedPair.p2.label;

                            // Determine if the tooltip includes the compared series
                            const comparedSeries = normalizeDatasetLabel(lastComparedPair.p1.datasetLabel);
                            const hasComparedSeries = tooltipItems.some((it) => {
                              const s = normalizeDatasetLabel(String(it.dataset?.label ?? ''));
                              return s === comparedSeries;
                            });

                            if (!isComparedMonth || !hasComparedSeries) return '';

                            return [
                              `Difference (Y2 - Y1): ${lastDifference.toFixed(2)}${lastDifferenceSuffix}`,
                              `Y1: ${lastComparedPair.p1.datasetLabel} • ${lastComparedPair.p1.label} = ${lastComparedPair.p1.y.toFixed(2)}`,
                              `Y2: ${lastComparedPair.p2.datasetLabel} • ${lastComparedPair.p2.label} = ${lastComparedPair.p2.y.toFixed(2)}`,
                            ];
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        display: true,
                        title: {
                          display: true,
                          text: 'Month',
                        },
                        grid: {
                          display: true,
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                      y: {
                        display: true,
                        beginAtZero: false,
                        title: {
                          display: true,
                          text: 'Amount (₹L)',
                        },
                        grid: {
                          display: true,
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                        ticks: {
                          callback: function(value) {
                            return '₹' + value + 'L';
                          },
                        },
                      },
                    },
                    interaction: {
                      intersect: false,
                    },
                  }}
                />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* GM% Trend Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 500 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Gross margin, EBITDA, PBT (Percentage)
            </Typography>
            <Box sx={{ height: 400 }}>
              {pnlData && (
                <Line
                  data={{
                    labels: getChartMonthLabels(),
                    datasets: [
                      {
                        label: 'Actual GM%',
                        data: getChartMonthLabels().map(month => {
                          // Convert month label to data key (e.g., "Apr-25" -> "apr_25")
                          const [monthName, year] = month.split('-');
                          const monthKey = `${monthName.toLowerCase()}_${year}`;
                          
                          if (pnlData.revenue?.[monthKey] && pnlData.cost_of_sales?.[monthKey]) {
                            return ((pnlData.revenue[monthKey] - pnlData.cost_of_sales[monthKey]) / pnlData.revenue[monthKey]) * 100;
                          }
                          return 0;
                        }),
                        borderColor: '#10B981',
                        backgroundColor: 'rgba(75, 192, 192, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#10B981',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                      {
                        label: 'Target GM%',
                        data: getChartMonthLabels().map(() => targetGM),
                        borderColor: 'rgb(255, 99, 132)',
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                      },
                      {
                      label: 'EBITDA %',
                      data: getChartMonthLabels().map(month => {
                        const [monthName, year] = month.split('-');
                        const monthKey = `${monthName.toLowerCase()}_${year}`;

                        const revenue = pnlData.revenue?.[monthKey];
                        const ebitda = pnlData.ebitda?.[monthKey];

                        if (!revenue || revenue === 0) return null;

                        return (ebitda / revenue) * 100;
                      }),
                      borderColor: '#F59E0B',
                      backgroundColor: 'rgba(75, 192, 192, 0.1)',
                      borderWidth: 3,
                      fill: true,
                      tension: 0.4,
                      pointBackgroundColor: '#F59E0B',
                      pointBorderColor: '#fff',
                      pointBorderWidth: 2,
                      pointRadius: 4,
                      pointHoverRadius: 4,
                    },
                    {
                      label: 'PBT %',
                      data: getChartMonthLabels().map(month => {
                        const [monthName, year] = month.split('-');
                        const monthKey = `${monthName.toLowerCase()}_${year}`;

                        const revenue = pnlData.revenue?.[monthKey];
                        const pbt = pnlData.pbt?.[monthKey];

                        if (!revenue || revenue === 0) return null;

                        return (pbt / revenue) * 100;
                      }),
                      borderColor: '#7a88b6ff',
                      backgroundColor: 'rgba(75, 192, 192, 0.1)',
                      borderWidth: 3,
                      fill: true,
                      tension: 0.4,
                      pointBackgroundColor: '#7a88b6ff',
                      pointBorderColor: '#fff',
                      pointBorderWidth: 2,
                      pointRadius: 4,
                      pointHoverRadius: 4,
                    }
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                      duration: 0,
                    },
                    hover: {
                      mode: 'nearest' as const,
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        labels: {
                          usePointStyle: true,
                          padding: 20,
                        },
                      },
                      title: {
                        display: false,
                      },
                  tooltip: {
                        enabled: true,
                        mode: 'index' as const,
                        intersect: false,
                        animation: false,
                        callbacks: {
                          label: function(context) {
                            const label = context.dataset.label || '';
                            const value = context.parsed.y ?? 0;

                            return `${label}: ${value.toFixed(1)}%`;
                          },

                          afterBody: function(context) {
                            if (!pnlData) return [];

                            const index = context[0].dataIndex;
                            const month = getChartMonthLabels()[index];
                            const [monthName, year] = month.split('-');
                            const monthKey = `${monthName.toLowerCase()}_${year}`;

                            const ebitda = pnlData.ebitda?.[monthKey];
                            const pbt = pnlData.pbt?.[monthKey];

                            return [
                              // `EBITDA: ₹${ebitda?.toLocaleString() ?? '-'}`,
                              // `PBT: ₹${pbt?.toLocaleString() ?? '-'}`
                            ];
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        display: true,
                        title: {
                          display: true,
                          text: 'Month',
                        },
                        grid: {
                          display: true,
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                      y: {
                        display: true,
                        beginAtZero: false,
                        title: {
                          display: true,
                          text: 'Percentage (%)',
                        },
                        grid: {
                          display: true,
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                        ticks: {
                          callback: function(value) {
                            return value + '%';
                          },
                        },
                      },
                    },
                    interaction: {
                      intersect: false,
                    },
                  }}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Delivery Costs and SGA */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Delivery Costs and SGA Chart */}
        <Grid item xs={12} md={6}>
                <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, height: { xs: 420, sm: 480, md: 500 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1.5, sm: 2 },
                      color: '#0f172a',
                      fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                    }}
                  >
                    SG&A, Delivery Costs (Amount)
                  </Typography>
                  <Box sx={{ height: { xs: 320, sm: 380, md: 400 } }}>
                    <Line
                      data={{
                        labels: (financeData?.monthly_data ?? []).map(d => d.month),
                        datasets: [
                          {
                            label: 'SG&A Expenses (₹)',
                            data: (financeData?.monthly_data ?? []).map(d => d.sga_expenses),
                            borderColor: '#c23493ff',
                            backgroundColor: 'rgba(220, 38, 38, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#c23493ff',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                          },
                          
                          {
                            label: 'Delivery Costs (₹)',
                            data: (financeData?.monthly_data ?? []).map(d => d.cost_of_sales),
                            borderColor: '#7c3aed',
                            backgroundColor: 'rgba(124, 58, 237, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#7c3aed',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                          },
                          // {
                          //   label: 'Travel Expenses (₹)',
                          //   data: (financeData?.monthly_data ?? []).map(d => d.travel_expenses),
                          //   borderColor: '#ea580c',
                          //   backgroundColor: 'rgba(234, 88, 12, 0.1)',
                          //   borderWidth: 3,
                          //   fill: false,
                          //   tension: 0.4,
                          //   pointBackgroundColor: '#ea580c',
                          //   pointBorderColor: '#ffffff',
                          //   pointBorderWidth: 2,
                          //   pointRadius: 6,
                          //   pointHoverRadius: 8,
                          // }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          mode: 'index' as const,
                          intersect: false,
                        },
                        plugins: {
                          legend: {
                            position: 'top' as const,
                            labels: {
                              usePointStyle: true,
                              padding: chartFont.legendPadding,
                              color: '#374151',
                              font: {
                                size: chartFont.legend,
                                weight: 'normal'
                              }
                            }
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            titleColor: '#ffffff',
                            bodyColor: '#ffffff',
                            borderColor: '#e5e7eb',
                            borderWidth: 1,
                            titleFont: {
                              size: chartFont.tooltipTitle,
                              weight: 'bold'
                            },
                            bodyFont: {
                              size: chartFont.tooltipBody
                            },
                            callbacks: {
                              label: function (context) {
                                return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              color: '#64748b',
                              font: {
                                size: chartFont.ticks,
                                weight: 'normal'
                              }
                            }
                          },
                          y: {
                            display: true,
                            beginAtZero: false,
                            position: 'left' as const,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.1)'
                            },
                            ticks: {
                              color: '#64748b',
                              font: {
                                size: chartFont.ticks,
                                weight: 'normal'
                              },
                              callback: function (value: any) {
                                return '₹' + (Number(value) / 100000).toFixed(1) + 'L';
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </Paper>
        </Grid>

        {/* Allocation Trends Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 500 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Allocation Trends (Percentage)
            </Typography>
            <Box sx={{ height: 400 }}>
              {data?.monthlyBreakdown && (
                <Line
                  data={{
                    labels: getChartMonthLabels(),
                    datasets: [
                      {
                        label: 'Billed %',
                        data: getChartMonthLabels().map(month => 
                          data.monthlyBreakdown.find((item: any) => item.month === month && item.category === 'billed')?.percentage || 0
                        ),
                        borderColor: '#0EA5E9',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        borderWidth: 3,
                        fill: false,
                        tension: 0.4,
                        pointBackgroundColor: '#0EA5E9',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                      {
                        label: 'Unbilled %',
                        data: getChartMonthLabels().map(month => 
                          data.monthlyBreakdown.find((item: any) => item.month === month && item.category === 'unbilled')?.percentage || 0
                        ),
                        borderColor: '#12f5cfec',
                        backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        borderWidth: 3,
                        fill: false,
                        tension: 0.4,
                        pointBackgroundColor: '#12f5cfec',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                      {
                        label: 'Bench %',
                        data: getChartMonthLabels().map(month => 
                          data.monthlyBreakdown.find((item: any) => item.month === month && item.category === 'bench')?.percentage || 0
                        ),
                        borderColor: '#EF4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        borderWidth: 3,
                        fill: false,
                        tension: 0.4,
                        pointBackgroundColor: '#EF4444',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                      duration: 0,
                    },
                    hover: {
                      mode: 'nearest' as const,
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        labels: {
                          usePointStyle: true,
                          padding: 20,
                        },
                      },
                      tooltip: {
                        enabled: true,
                        mode: 'index' as const,
                        intersect: false,
                        animation: false,
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed.y;
                            return `${context.dataset.label}: ${value.toFixed(1)}%`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        display: true,
                        title: {
                          display: true,
                          text: 'Month',
                        },
                        grid: {
                          display: true,
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                      y: {
                        display: true,
                        beginAtZero: false,
                        title: {
                          display: true,
                          text: 'Percentage (%)',
                        },
                        grid: {
                          display: true,
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                        ticks: {
                          callback: function(value) {
                            return value + '%';
                          },
                        },
                      },
                    },
                    interaction: {
                      intersect: false,
                    },
                  }}
                />
              )}
            </Box>
          </Paper>
        </Grid>

      </Grid>


    </Box>
  );
};

export default ExecutiveSummaryPage;